export type AnalyticsPreference = "loading" | "default" | "accepted" | "declined" | "privacy-signal";

const COOKIE = "dbm-analytics-consent";
export const ANALYTICS_PREFERENCE_EVENT = "dbm-analytics-consent-change";

export function resolveAnalyticsPreference(value: string | null, privacySignal = false): AnalyticsPreference {
  if (privacySignal) return "privacy-signal";
  return value === "accepted" || value === "declined" ? value : "default";
}

export function getAnalyticsPreference(): AnalyticsPreference {
  if (typeof document === "undefined") return "loading";
  const cookie = document.cookie.split(";").map((part) => part.trim()).find((part) => part.startsWith(`${COOKIE}=`))?.split("=")[1];
  let saved = cookie === "accepted" || cookie === "declined" ? cookie : null;
  if (!saved) {
    try { saved = window.localStorage.getItem(COOKIE); } catch { /* Cookies can still store the choice. */ }
  }
  const browser = navigator as Navigator & { globalPrivacyControl?: boolean };
  return resolveAnalyticsPreference(saved, browser.globalPrivacyControl === true || browser.doNotTrack === "1" || browser.doNotTrack === "yes");
}

/** Production providers must never collect localhost, LAN, staging or preview traffic. */
export function isProductionTrackingLocation(location: { hostname: string; protocol: string; port: string } | undefined): boolean {
  if (!location) return false;
  const hostname = location.hostname.toLowerCase();
  return location.protocol === "https:" && !location.port &&
    (hostname === "databidmachine.com" || hostname === "www.databidmachine.com");
}

export function analyticsEnabled() {
  if (typeof window === "undefined" || !isProductionTrackingLocation(window.location)) return false;
  const preference = getAnalyticsPreference();
  return preference === "default" || preference === "accepted";
}

export function subscribeToAnalyticsPreference(onChange: () => void) {
  window.addEventListener(ANALYTICS_PREFERENCE_EVENT, onChange);
  return () => window.removeEventListener(ANALYTICS_PREFERENCE_EVENT, onChange);
}

export function saveAnalyticsPreference(value: "accepted" | "declined") {
  document.cookie = `${COOKIE}=${value}; Max-Age=31536000; Path=/; SameSite=Lax${window.location.protocol === "https:" ? "; Secure" : ""}`;
  try { window.localStorage.setItem(COOKIE, value); } catch { /* Cookie is the primary preference store. */ }
  window.dispatchEvent(new Event(ANALYTICS_PREFERENCE_EVENT));
}
